import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import FieldDropdown from "../../../../components/common/InputField/FieldDropdown";
import { Field } from "formik";
import {
  BUSINESS_FORM_ACTION_ROW_BETWEEN_CLASS,
} from "../../shared/businessLayoutSpec";
import {
  AdminHeaderAction,
  AdminLinkAction,
  AdminFormFieldError,
  AdminFormFieldLabelRow,
  AdminDetailFieldLabel,
  adminWizardDropdownClasses,
  adminWizardInputClasses,
} from "../../../AdminDashboard/shared/adminUx";
import { fetchServiceCategories } from "../../../../Redux/Slice/serviceCategoriesSlice";
import { useGraphQL } from "../../../../context/GraphQLProvider";
import { updatePostData } from "../../../../Redux/Slice/createPostSlice";
import useAuth from "../../../../Hooks/useAuth";

const MUTED_DROPDOWN_PANEL = "custom-dropdown-panel !bg-mistygray";

const DefineCategory = ({ values, setFieldValue, setStep, handlePost }) => {
  const dispatch = useDispatch();
  const graphqlService = useGraphQL();
  const { auth } = useAuth();
  const { categories } = useSelector((state) => state.serviceCategories);
  const { serviceCategory } = useSelector((state) => state.user);
  const postData = useSelector((state) => state.createPost);
  const [isUpdating, setIsUpdating] = useState(false);
  const initialLoad = useRef(true);

  const isProPlan = auth?.userPlan?.plan?.name?.toLowerCase() === "pro";

  useEffect(() => {
    if (categories.length === 0) {
      dispatch(fetchServiceCategories(graphqlService));
    }
  }, [dispatch, graphqlService, categories.length]);

  useEffect(() => {
    if (categories.length > 0 && serviceCategory && !values.mainCategory) {
      const userCategory =
        categories.find((cat) => cat.id == serviceCategory.id) ||
        categories.find(
          (cat) =>
            (cat.name || "").trim().toLowerCase() ===
            (serviceCategory.name || "").trim().toLowerCase()
        );
      if (userCategory) {
        setFieldValue("mainCategory", userCategory);
      }
    }
  }, [categories, serviceCategory, setFieldValue, values.mainCategory]);

  const subListing =
    categories?.length > 0
      ? categories.find(
          (item) => (item.name || "").trim() === (values.mainCategory?.name || "").trim()
        )
      : null;

  const getSubcategoryOptions = () => {
    const baseOptions = subListing ? subListing.subcategories : [];
    return [...baseOptions, { name: "Create New", id: "custom" }];
  };

  const handleSubcategoryChange = (e) => {
    const selectedValue = e.value;

    if (selectedValue.id !== "custom") {
      setFieldValue("customSubcategory", "");
    }

    if (selectedValue.id !== "custom" && subListing) {
      const selectedSubcategory = subListing.subcategories.find(
        (sub) => sub.name === selectedValue.name
      );
      if (selectedSubcategory) {
        dispatch(
          updatePostData({
            postTags: initialLoad.current ? postData.postTags : [],
            postKeywordsSuggestions: selectedSubcategory.keywords
              ? selectedSubcategory.keywords.split(",")
              : [],
          })
        );
      }
      initialLoad.current = false;
    }

    setFieldValue("subCategory", selectedValue);
  };

  useEffect(() => {
    if (values.subCategory && initialLoad.current) {
      handleSubcategoryChange({ value: values.subCategory });
    }
  }, [values.subCategory]);

  const handleNextClick = () => {
    if (
      values.mainCategory &&
      values.subCategory &&
      (values.subCategory.id !== "custom" || values.customSubcategory)
    ) {
      dispatch(
        updatePostData({
          mainCategory: values.mainCategory.id,
          subCategory: values.subCategory.id,
          customSubcategory: values.customSubcategory,
        })
      );
      setIsUpdating(true);
    }
  };

  useEffect(() => {
    if (isUpdating && postData.mainCategory) {
      handlePost();
      setIsUpdating(false);
      setStep(3);
    }
  }, [isUpdating, postData.mainCategory]);

  return (
    <>
      <div className="w-full">
      <div>
        <AdminFormFieldLabelRow
          label="Primary Category"
          required
          tooltip="Your primary category is set based on your business type and cannot be changed."
        />

        <Field name="mainCategory">
          {({ form }) => (
            <>
              <FieldDropdown
                surface="muted"
                fullWidth
                value={values.mainCategory}
                onChange={(e) => setFieldValue("mainCategory", e.value)}
                options={categories}
                optionLabel="name"
                placeholder="Post primary category"
                disabled
                showChevron={false}
                className={adminWizardDropdownClasses(false, true)}
                panelClassName={MUTED_DROPDOWN_PANEL}
              />
              {form.touched.mainCategory && form.errors.mainCategory ? (
                <AdminFormFieldError className="mt-2">{form.errors.mainCategory}</AdminFormFieldError>
              ) : null}
            </>
          )}
        </Field>
      </div>

      <div className="mt-5">
        <AdminFormFieldLabelRow
          label="Subcategory"
          required
          tooltip="Choose a subcategory from the list, or create a new one if none fit."
        />

        <Field name="subCategory">
          {({ form }) => (
            <>
              <FieldDropdown
                surface="muted"
                fullWidth
                value={values.subCategory}
                onChange={handleSubcategoryChange}
                options={getSubcategoryOptions()}
                optionLabel="name"
                placeholder="Select a subcategory"
                className={adminWizardDropdownClasses(
                  Boolean(form.touched.subCategory && form.errors.subCategory),
                  true
                )}
                panelClassName={MUTED_DROPDOWN_PANEL}
              />

              {values.subCategory?.id === "custom" ? (
                <div className="mt-3">
                  <Field name="customSubcategory">
                    {({ field: subField, form: subForm }) => {
                      const hasError = Boolean(
                        subForm.touched.customSubcategory && subForm.errors.customSubcategory
                      );
                      return (
                        <div className="space-y-1 w-full min-w-0">
                          <AdminDetailFieldLabel htmlFor="customSubcategory">
                            New Subcategory
                          </AdminDetailFieldLabel>
                          <input
                            {...subField}
                            id="customSubcategory"
                            type="text"
                            value={subField.value || ""}
                            placeholder="Enter subcategory (10–40 characters)"
                            className={`${adminWizardInputClasses(hasError)} lg:bg-pastalblue`}
                          />
                          {hasError ? (
                            <AdminFormFieldError>{subForm.errors.customSubcategory}</AdminFormFieldError>
                          ) : null}
                        </div>
                      );
                    }}
                  </Field>
                </div>
              ) : null}

              {form.touched.subCategory && form.errors.subCategory ? (
                <AdminFormFieldError className="mt-2">{form.errors.subCategory}</AdminFormFieldError>
              ) : null}
            </>
          )}
        </Field>
      </div>

      </div>

      <div className={BUSINESS_FORM_ACTION_ROW_BETWEEN_CLASS}>
        {isProPlan ? (
          <AdminLinkAction type="button" onClick={() => setStep(1)}>
            Back
          </AdminLinkAction>
        ) : (
          <div />
        )}
        <AdminHeaderAction
          type="button"
          onClick={handleNextClick}
          disabled={
            !values.mainCategory ||
            !values.subCategory ||
            (values.subCategory.id === "custom" &&
              (!values.customSubcategory ||
                values.customSubcategory.length < 10 ||
                values.customSubcategory.length > 40))
          }
        >
          Next
        </AdminHeaderAction>
      </div>
    </>
  );
};

export default DefineCategory;
